import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Stack } from 'src/components/Stack';
import { useSession } from 'src/contexts/SessionContext';
import { CartItem as CartItemType } from 'src/models/cart/_types';
import { searchCartItem } from 'src/models/cart/cart';
import { User } from 'src/models/user/_types';
import { selectableItemTypes } from 'src/shared/constants/itemTypes';
import { Identifier, WithSearchResult } from 'src/types/utilityTypes';
import { useListSearchParams } from 'src/utils/useListSearchParams';

import ItemFilters from '../../pages/Clients/PaymentDialog/ItemFilters';
import { SearchAndAddItem } from '../../pages/Clients/PaymentDialog/SearchAndAddItem';

interface ItemBrowserProps<T extends { id: Identifier }> {
  user?: User;
  selectedItemType?: string | null;
  searchValue: string;
  onSearchChange: (value: string) => void;
  debouncedSearchValue: string;
  onSelect: (item: WithSearchResult<T>) => void;
  onRemove: (item: CartItemType, index?: number) => void;
  cartItems: CartItemType[];
  includeOnlineTraining?: boolean;
  checkIfItemAdded?: (item: SearchItem) => boolean;
}

interface SearchItem extends CartItemType {
  id: Identifier;
  name: string;
  aliases?: string[];
  header?: string;
  type: string;
}

export const ItemBrowser = <T extends { id: Identifier }>({
  user,
  selectedItemType: selectedItemTypeValue,
  searchValue,
  onSearchChange,
  debouncedSearchValue,
  onSelect,
  onRemove,
  cartItems,
  includeOnlineTraining = false,
  checkIfItemAdded,
}: ItemBrowserProps<T>) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'common.itemTypes',
  });
  const { t: tPayment } = useTranslation('translation', {
    keyPrefix: 'client.payment',
  });
  const { session } = useSession();
  const {
    filterParams,
    resetFilterParams,
    params,
    updateParam,
    updateFilterParam,
    getFilterParamValue,
  } = useListSearchParams({
    paramPrefix: 'p_',
  });

  // Find the selected ItemType object from the string value
  const selectedItemType = selectedItemTypeValue
    ? selectableItemTypes.find(
        (type) => type.value === selectedItemTypeValue
      ) || null
    : null;

  const getHeaderForType = (type: string) => {
    switch (type) {
      case 'onlineTraining':
        return t('onlineTraining');
      case 'group':
        return t('groups');
      case 'package':
        return t('packages');
      case 'product':
        return t('products');
      case 'plan':
        return t('plans');
      default:
        return type.charAt(0).toUpperCase() + type.slice(1) + 's';
    }
  };

  const activeFilters =
    searchValue ||
    filterParams.paymentPackageTags ||
    filterParams.paymentProductTag ||
    filterParams.packageLocation;

  const resetFilters = () => {
    onSearchChange('');
    resetFilterParams();
  };

  const { data: searchResults = [], isLoading } = useQuery({
    queryKey: [
      'cartSearch',
      debouncedSearchValue,
      selectedItemType?.value,
      user?.id,
      filterParams.productTag,
      filterParams.packageLocation,
      filterParams.packageCategory,
    ],
    queryFn: async () => {
      const results = await searchCartItem({
        search: debouncedSearchValue,
        types: selectedItemType?.value ? [selectedItemType.value] : [],
        params: {
          exclude_purchased_user_id: user?.id,
          package_tags: filterParams.packageCategory || null,
          product_tags: filterParams.productTag || null,
          location_ids: filterParams.packageLocation
            ? [filterParams.packageLocation]
            : null,
        },
      });

      // Create the online training item
      const onlineTrainingItem = {
        id: user?.trainerAssociationId || '',
        name: tPayment('onlineTraining'),
        itemId: user?.trainerAssociationId || '',
        itemType: 'onlineTraining',
        type: 'onlineTraining',
        item: {
          id: user?.trainerAssociationId || '',
          type: 'client',
          paymentOptions: session?.paymentOptions || [],
          paymentOption: session?.paymentOptions?.[0],
          imageUrl: '',
          name: tPayment('onlineTraining'),
          item: {
            id: user?.trainerAssociationId || '',
            type: 'client',
            name: tPayment('onlineTraining'),
          } as any,
        },
        quantity: 1,
        aliases: [
          tPayment('searchAliases.onlineTraining'),
          tPayment('searchAliases.trainingSubscription'),
        ],
        header: tPayment('onlineTraining'),
      } as SearchItem;

      // Add online training to results if:
      // 1. includeOnlineTraining is true
      // 2. Online Training type is specifically selected
      // 3. Search term is empty or matches "online training"
      const shouldIncludeOnlineTraining =
        includeOnlineTraining &&
        (selectedItemType?.value === 'onlineTraining' ||
          (selectedItemType?.value === null &&
            (!debouncedSearchValue ||
              `${tPayment('searchAliases.onlineTraining')} ${tPayment(
                'searchAliases.trainingSubscription'
              )}`.includes(debouncedSearchValue.toLowerCase()))));

      const mappedResults = results.map((item) => ({
        ...item,
        id: item.id,
        name: item.name || '',
        aliases: item.aliases || [],
        header: getHeaderForType(item.type),
        type: item.type,
      }));

      return shouldIncludeOnlineTraining
        ? [onlineTrainingItem, ...mappedResults]
        : mappedResults || [];
    },
    staleTime: 30000, // Consider results fresh for 30 seconds
    cacheTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  return (
    <Stack spacing={2}>
      <ItemFilters
        resetFilterParams={resetFilterParams}
        params={params}
        updateParam={updateParam}
        updateFilterParam={updateFilterParam}
        getFilterParamValue={getFilterParamValue}
        selectedItemType={selectedItemTypeValue}
      />
      <SearchAndAddItem
        onAdd={(item) => onSelect(item as WithSearchResult<T>)}
        onRemove={onRemove}
        itemList={cartItems as CartItemType[]}
        isLoading={isLoading}
        searchResults={searchResults}
        activeFilters={activeFilters}
        resetFilters={resetFilters}
        selectedItemType={selectedItemType}
        cartItems={cartItems}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        checkIfItemAdded={checkIfItemAdded}
      />
    </Stack>
  );
};
